import { useInfiniteQuery } from '@tanstack/react-query';
import { findActionIn, invokeAction } from 'actions';
import { useAuthStatus } from 'features/auth';
const DEFAULT_PAGE_SIZE = 50;
/**
 * Paginated list of agents currently assigned to a given project. Reads the
 * per-record `listAssignedAgents` action from the project's `potentialAction`.
 * The action's URL template needs the project's full `id` URI as `object`.
 */
export function useListAssignedAgents({ project, searchTerm, status }) {
    const authStatus = useAuthStatus();
    const user = authStatus.kind === 'authenticated' ? authStatus.user : null;
    const action = findActionIn(project.potentialAction, 'listAssignedAgents');
    const projectUUID = project.UUID || '';
    const projectIdURI = project.id || '';
    const sendSearch = searchTerm.length >= 2 ? searchTerm : '';
    const sendStatus = status === 'all' ? '' : status;
    return useInfiniteQuery({
        queryKey: ['listAssignedAgents', user?.email, projectUUID, sendSearch, sendStatus],
        enabled: Boolean(user && action && projectIdURI),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const view = lastPage.response.filteredView;
            const total = view?.numberOfItems ?? 0;
            const pageSize = view?.pageSize ?? DEFAULT_PAGE_SIZE;
            const current = view?.currentPage ?? lastPage.page;
            if (current * pageSize >= total)
                return undefined;
            return current + 1;
        },
        queryFn: async ({ pageParam }) => {
            if (!user || !action)
                throw new Error('listAssignedAgents unavailable');
            const variables = {
                object: projectIdURI,
                currentPage: String(pageParam),
                pageSize: String(DEFAULT_PAGE_SIZE),
            };
            if (sendSearch)
                variables.searchTerm = sendSearch;
            if (sendStatus)
                variables.status = sendStatus;
            const response = await invokeAction(action, user.token, { variables });
            if (!response.ok)
                throw new Error(`listAssignedAgents returned ${response.status}`);
            const json = (await response.json());
            return { response: json, page: pageParam };
        },
    });
}
